/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import {
  Attention,
  RenderAmount,
  Time,
  useBankCoreApiContext,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { format, isToday, isYesterday } from "date-fns";
import { Fragment, VNode, h } from "preact";
import { State, Transaction } from "./index.js";

export function FailedView({ title, onRetry }: State.Failed): VNode {
  const { i18n } = useTranslationContext();
  return (
    <Attention type="danger" title={title}>
      <button type="button" class="font-semibold underline" onClick={onRetry}>
        <i18n.Translate>Try again</i18n.Translate>
      </button>
    </Attention>
  );
}

function dayLabel(
  transaction: Transaction,
  locale: Locale,
  today: string,
  yesterday: string,
): string {
  if (transaction.when.t_ms === "never") return "";
  const date = new Date(transaction.when.t_ms);
  if (isToday(date)) return today;
  if (isYesterday(date)) return yesterday;
  return format(date, "P", { locale });
}

export function ReadyView({
  transactions,
  variant,
  title,
  routeFullHistory,
  routeBack,
  onGoNext,
  onGoPrevious,
}: State.Ready): VNode {
  const { i18n, dateLocale } = useTranslationContext();
  // Translators: Bank-transfer subject or payment reference.
  const subjectLabel = i18n.str`Subject`;
  const { config } = useBankCoreApiContext();
  const groups = transactions.reduce<Record<string, Transaction[]>>(
    (result, transaction) => {
      const key = dayLabel(
        transaction,
        dateLocale,
        i18n.str`Today`,
        i18n.str`Yesterday`,
      );
      (result[key] ??= []).push(transaction);
      return result;
    },
    {},
  );

  function TransactionAmount({
    transaction,
  }: {
    transaction: Transaction;
  }): VNode {
    return transaction.amount ? (
      <span class="whitespace-nowrap text-sm font-semibold">
        <RenderAmount
          value={transaction.amount}
          negative={transaction.negative}
          withColor
          withSign
          spec={config.currency_specification}
        />
      </span>
    ) : (
      <span class="text-sm text-secondary">
        <i18n.Translate>Invalid value</i18n.Translate>
      </span>
    );
  }

  return (
    <section class="mt-8" aria-labelledby={`${variant}-activity-heading`}>
      {variant === "history" && routeBack ? (
        <a
          class="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-primaryDark hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          href={routeBack.url({})}
        >
          <svg
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
            />
          </svg>
          <i18n.Translate>Back to overview</i18n.Translate>
        </a>
      ) : undefined}
      <div class="flex items-center justify-between gap-4">
        <h2
          id={`${variant}-activity-heading`}
          class="text-lg font-semibold text-onBackground"
        >
          {title}
        </h2>
        {variant === "recent" && routeFullHistory ? (
          <a
            class="text-sm font-semibold text-primaryDark hover:underline"
            href={routeFullHistory.url({})}
          >
            <i18n.Translate>View all transactions</i18n.Translate>
          </a>
        ) : undefined}
      </div>

      {!transactions.length ? (
        <div class="mt-4">
          <Attention type="low" title={i18n.str`No transactions yet.`}>
            <i18n.Translate>
              Your account activity will appear here.
            </i18n.Translate>
          </Attention>
        </div>
      ) : variant === "recent" ? (
        <div class="mt-4 overflow-hidden rounded-lg border border-outlineVariant bg-white">
          {Object.entries(groups).map(([date, items]) => (
            <Fragment key={date}>
              <h3 class="border-b border-outlineVariant bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-secondary">
                {date}
              </h3>
              <ul class="divide-y divide-outlineVariant">
                {items.map((item) => (
                  <li
                    key={item.id}
                    class="grid gap-1 px-4 py-3 sm:grid-cols-[5rem_1fr_auto] sm:items-center sm:gap-4"
                  >
                    <span class="text-sm text-secondary">
                      <Time format="HH:mm" timestamp={item.when} />
                    </span>
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-onBackground">
                        {item.counterpart}
                      </p>
                      <p class="truncate text-sm text-secondary">
                        {item.subject}
                      </p>
                    </div>
                    <TransactionAmount transaction={item} />
                  </li>
                ))}
              </ul>
            </Fragment>
          ))}
        </div>
      ) : (
        <Fragment>
          <div class="mt-4 hidden overflow-hidden rounded-lg border border-outlineVariant sm:block">
            <table class="min-w-full divide-y divide-outlineVariant">
              <thead class="bg-gray-50">
                <tr>
                  <th
                    class="px-4 py-3 text-left text-sm font-semibold"
                    scope="col"
                  >
                    <i18n.Translate>Date</i18n.Translate>
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-semibold"
                    scope="col"
                  >
                    <i18n.Translate>Counterparty</i18n.Translate>
                  </th>
                  <th
                    class="px-4 py-3 text-left text-sm font-semibold"
                    scope="col"
                  >
                    {subjectLabel}
                  </th>
                  <th
                    class="px-4 py-3 text-right text-sm font-semibold"
                    scope="col"
                  >
                    <i18n.Translate>Amount</i18n.Translate>
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-outlineVariant bg-white">
                {transactions.map((item) => (
                  <tr key={item.id}>
                    <td class="whitespace-nowrap px-4 py-3 text-sm text-secondary">
                      {item.when.t_ms === "never"
                        ? ""
                        : format(item.when.t_ms, "P", {
                            locale: dateLocale,
                          })}{" "}
                      <Time format="HH:mm" timestamp={item.when} />
                    </td>
                    <td class="px-4 py-3 text-sm font-medium">
                      {item.counterpart}
                    </td>
                    <td class="max-w-md truncate px-4 py-3 text-sm text-secondary">
                      {item.subject}
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-right">
                      <TransactionAmount transaction={item} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul class="mt-4 divide-y divide-outlineVariant overflow-hidden rounded-lg border border-outlineVariant sm:hidden">
            {transactions.map((item) => (
              <li key={item.id} class="space-y-1 bg-white px-4 py-3">
                <div class="flex justify-between gap-3">
                  <p class="font-semibold">{item.counterpart}</p>
                  <TransactionAmount transaction={item} />
                </div>
                <p class="text-sm text-secondary">{item.subject}</p>
                <p class="text-xs text-secondary">
                  {item.when.t_ms === "never"
                    ? ""
                    : format(item.when.t_ms, "P", { locale: dateLocale })}{" "}
                  <Time format="HH:mm" timestamp={item.when} />
                </p>
              </li>
            ))}
          </ul>
          <nav
            class="mt-4 flex justify-between"
            aria-label={i18n.str`Pagination`}
          >
            <button
              type="button"
              disabled={!onGoPrevious}
              onClick={onGoPrevious}
              class="rounded-md border border-outlineVariant px-3 py-2 text-sm font-semibold disabled:opacity-40"
            >
              <i18n.Translate>Newer</i18n.Translate>
            </button>
            <button
              type="button"
              disabled={!onGoNext}
              onClick={onGoNext}
              class="rounded-md border border-outlineVariant px-3 py-2 text-sm font-semibold disabled:opacity-40"
            >
              <i18n.Translate>Older</i18n.Translate>
            </button>
          </nav>
        </Fragment>
      )}
    </section>
  );
}
